import * as React from "react";
import TooltipOptions from "@bizform/primereact/components/tooltip/TooltipOptions";
import {Static} from "@bizform/type/decorator/Static.decorator";
import {WindowKey} from "@bizform/type/model/model/WindowKey.model";
import {
    BaseStateModel,
    ComponentElement,
    ComponentElementDisplayMode,
    ComponentElementStatic
} from "@bizform/type/model/component_element/ComponentElement.model";
import {ExceptionType} from "@bizform/type/model/model/Exception.model";
import {
    ComponentCategory,
    ComponentElementManifest,
    ComponentElementProperty
} from "@bizform/type/model/component_element/ComponentElementManifest.model";
import {DataType} from "@bizform/type/model/model/DataType.model";
import {ComponentHelper, Debug, FileHelper, ObjectHelper} from "@bizform/helper/dist/js/bizflow/helper.min";
import {
    ComponentElementClass,
    ExceptionHandler,
    I18N,
    ReactComponentElementInit
} from "@bizform/core/dist/js/bizflow/core.min";
import {AbstractComponentElement} from "../AbstractComponentElement";
import {I18n} from "../../misc/model/I18n.model";


type Props = {
};

type WrappedCompState = {
    id?: string;
    tabindex?: number;
    useCustomLinkName?: boolean;
    linkName?: string;
    linkURL?: string;
    linkTarget?: string;
    label?: string;
    style?: object;
    className?: string;
    role?: string;
    tooltipAttribute?: TooltipOptions & {text?: string};
    onClick?: (e) => void;
};

type WrappedFormState = {
    onClick?: string;
};

type State = BaseStateModel & {
    wrappedCompState: WrappedCompState;
    wrappedFormState: WrappedFormState;
};

/**
 * @since 0.0.1
 * @author Byungmin Choi, Hwansoon Park
 * @class BfLink
 * @description BfLink component element
 */
@ComponentElementClass()
@Static<ComponentElementStatic<null>>()
export class BfLink extends AbstractComponentElement<Props, State> {
    /* manifest */
    static readonly componentElementManifest: ComponentElementManifest = {
        uniqueId: "_gkzQy8CkvIxvoiQIWxFrJOr7n9H9AgM",
        category: ComponentCategory.LABEL,
        label: I18n.componentElement.link.label,
        description: I18n.componentElement.link.description,
        icon: "",
        dataType: [DataType.STRING],
        property: null
    };

    focus(): void {
        if (this._link) {
            this._link.focus();
        }
    }

    setProperty(property: ComponentElementProperty): void {
        try {
            this._removeCustom();
            const newState = ComponentHelper.createStateWithProperties<State, React.FormEvent<HTMLInputElement>>(
                property,
                this);
            if (!newState.wrappedCompState.role) {
                delete(newState.wrappedCompState.role);
            }
            if (newState.wrappedCompState.linkURL) {
                newState.wrappedCompState.linkURL = FileHelper.getUrl(newState.wrappedCompState.linkURL);
            }
            this.setState(newState);
        } catch (e) {
            this.emitDidCatch(e);
        }
    }

    emitFormState(value: string): void {
    }

    emitHasContainerKey(value: string): void {
    }

    subscription(value: string): void {
        const newState = {
            wrappedCompState: {
                ...this.state.wrappedCompState,
                linkURL: FileHelper.getUrl(value)
            }
        };
        this.setState(newState);
    }

    private _onClickEvent(event): void {
    }

    noDisplayInheritance() {
    }

    @ReactComponentElementInit(BfLink)
    static init(): ComponentElement<null> {
        return;
    }

    initDone(): void {
    }

    destroy(): void {
        // proceed something to clean up
        this.destroyDone();
    }

    destroyDone(): void {
    }

    /* for React */
    props: Props;
    state: State;

    private _link: any;
    private _tooltip: Array<any> = [];

    constructor(props?: Props) {
        super(props);

        this.state = {
            show: false,
            displayMode: ComponentElementDisplayMode.INIT,
            wrappedCompState: {
                linkName: null,
                linkURL: null
            },
            wrappedFormState: {
                onClick: null
            }
        };

        this._notify = this._notify.bind(this);
        this._setRef = this._setRef.bind(this);
        this._onClickEvent = this._onClickEvent.bind(this);
    }

    shouldComponentUpdate(nextProps: Readonly<Props>, nextState: Readonly<State>, nextContext: any): boolean {
        return ObjectHelper.hasDifference(nextState, this.state);
    }

    componentWillUnmount(): void {
        this._removeCustom();
        this._link = null;
    }

    componentDidUpdate(prevProps) {
        if (this._link && !this._tooltip.length) {
            ComponentHelper.tooltip(this._link, this.state.wrappedCompState.tooltipAttribute, this._tooltip);
        }
    }

    componentDidCatch(error: Error, errorInfo: React.ErrorInfo): void {
        this.emitDidCatch(error);
    }

    emitDidCatch(error: Error | any): void {
        Debug.console.error(error);
    }

    render(): React.ReactNode {
        const {
            show,
            displayMode
        } = this.state;

        if (!show) {
            return null;
        } else {
            switch (displayMode) {
                case ComponentElementDisplayMode.EDIT:
                case ComponentElementDisplayMode.READONLY:
                case ComponentElementDisplayMode.DISABLED:
                case ComponentElementDisplayMode.PRINT:
                    return this._render();
                default:
                    return null;
            }
        }
    }

    private _render(): React.ReactNode {
        let value: any = this.state.wrappedCompState.linkName;
        if (this.state.wrappedCompState.useCustomLinkName) {
            value = <div dangerouslySetInnerHTML={this.createMarkup()} />;
        } else {
            value = this.state.wrappedCompState.linkName;
        }
        const newProps = {...this.state.wrappedCompState} as any;
        if (newProps.label) {
            newProps["aria-label"] = newProps.label;
        }
        delete(newProps.linkName);
        delete(newProps.useCustomLinkName);
        delete(newProps.linkURL);
        delete(newProps.linkTarget);
        delete(newProps.tooltipAttribute);
        delete(newProps.label);
        if (window[WindowKey.DESIGNMODE]) {
            return (
                <a
                    {...newProps}
                    key={this.elementId}
                    id={this.elementId}
                    ref={this._setRef}
                    style={{ ...this.state.wrappedCompState.style }}
                    className={this.state.wrappedCompState.className}
                    onClick={this._notify}
                >
                    {value}
                </a>
            );
        } else {
            return (
                <a
                    {...newProps}
                    key={this.elementId}
                    id={this.elementId}
                    href={this.state.wrappedCompState.linkURL}
                    target={this.state.wrappedCompState.linkTarget}
                    ref={this._setRef}
                    style={{...this.state.wrappedCompState.style}}
                    className={this.state.wrappedCompState.className}
                    onClick={this.state.wrappedCompState.onClick}
                >
                    {value}
                </a>
            );
        }
    }

    private _notify() {
        ExceptionHandler.toast(ExceptionType.INFO, I18N.translate("{$I._app.accessibility.component.link.designTime}"));
    }

    private createMarkup() {
        return {__html: this.state.wrappedCompState.linkName};
    }

    private _setRef(ref) {
        if (ref) {
            this._removeCustom();
            this._link = ref;
            this.readyComponent();
        }
    }

    private _removeCustom(): void {
        if (this._tooltip.length) {
            ComponentHelper.unbindTooltip(this._tooltip);
        }
    }
}
